'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
  {
    id: 't1',
    quote:
      "Kavita's coaching completely transformed how I present myself. Within 3 months, I went from dreading public speaking to keynoting at our company's all-hands. Her Voice Method is unlike anything I've experienced.",
    name: 'Arjun Mehta',
    role: 'VP of Product, Razorpay',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fb00d656-1763291827615.png',
    imageAlt: 'Professional headshot of Arjun Mehta, VP of Product',
    category: 'Coaching',
  },
  {
    id: 't2',
    quote:
      "Her performance at our Diwali gala left every single guest in tears. Kavita doesn't just sing — she creates an entire emotional world. We've had her back three times now.",
    name: 'Priya Nair',
    role: 'Events Director, Taj Hotels Pune',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c20d6ce4-1763294854382.png',
    imageAlt: 'Professional headshot of Priya Nair, Events Director',
    category: 'Performance',
  },
  {
    id: 't3',
    quote:
      "Reading 'Echoes of Silence' was a turning point for me. Kavita writes the way she speaks — with total precision and total warmth. I've gifted this book to everyone I care about.",
    name: 'Rahul Sharma',
    role: 'Literary Editor, HarperCollins India',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png',
    imageAlt: 'Professional headshot of Rahul Sharma, Literary Editor',
    category: 'Writing',
  },
];

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.will-animate');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const t = testimonials?.[active];

  return (
    <section id="testimonials" className="py-32 lg:py-40 overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-6 mb-20">
          <span className="section-label scroll-reveal will-animate">Voices</span>
          <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        </div>

        {/* Featured quote */}
        <div
          className="relative rounded-[48px] overflow-hidden p-10 lg:p-20 scroll-reveal will-animate"
          style={{ background: '#F2E8DF' }}
        >
          {/* Background quote mark */}
          <div className="absolute top-8 left-10 select-none pointer-events-none">
            <span className="quote-mark">&ldquo;</span>
          </div>

          <div className="relative z-10 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <div className="mb-4">
                <span
                  className="text-10 font-bold tracking-widest uppercase px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(184,150,110,0.15)', color: 'var(--gold)' }}
                >
                  {t?.category}
                </span>
              </div>
              <blockquote
                className="font-fraunces font-light leading-snug text-charcoal"
                style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2.2rem)' }}
              >
                &ldquo;{t?.quote}&rdquo;
              </blockquote>
              <div className="flex items-center gap-4 mt-8">
                <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={t?.image}
                    alt={t?.imageAlt}
                    fill
                    className="object-cover"
                    sizes="48px"
                  />
                </div>
                <div>
                  <p className="font-semibold text-charcoal text-sm">{t?.name}</p>
                  <p className="text-muted text-xs mt-0.5">{t?.role}</p>
                </div>
              </div>
            </div>

            {/* Navigation dots + mini cards */}
            <div className="lg:col-span-4 flex flex-col gap-4">
              {testimonials?.map((item, i) => (
                <button
                  key={item?.id}
                  onClick={() => setActive(i)}
                  className={`text-left p-4 rounded-2xl transition-all duration-300 ${
                    i === active
                      ? 'bg-charcoal text-cream shadow-luxury'
                      : 'bg-white/60 hover:bg-white text-charcoal'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                      <AppImage
                        src={item?.image}
                        alt={item?.imageAlt}
                        fill
                        className="object-cover"
                        sizes="36px"
                      />
                    </div>
                    <div>
                      <p
                        className={`font-semibold text-xs ${i === active ? 'text-cream' : 'text-charcoal'}`}
                      >
                        {item?.name}
                      </p>
                      <p
                        className={`text-10 mt-0.5 ${i === active ? 'text-cream/60' : 'text-muted'}`}
                      >
                        {item?.role?.split(',')?.[0]}
                      </p>
                    </div>
                  </div>
                  {i === active && (
                    <p className="text-cream/70 text-xs mt-2 leading-relaxed line-clamp-2">
                      {item?.quote?.substring(0, 80)}...
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {[
            { num: '500+', label: 'Coaching Clients', sub: 'Across 14 countries' },
            { num: '4.9★', label: 'Average Rating', sub: 'From 200+ reviews' },
            { num: '3', label: 'Books Published', sub: 'HarperCollins & Penguin' },
            { num: '60+', label: 'Live Performances', sub: 'Nationally & internationally' },
          ]?.map((s, i) => (
            <div
              key={s?.label}
              className="scroll-reveal will-animate p-6 rounded-3xl text-center"
              style={{
                background: 'rgba(250,246,240,0.8)',
                border: '1px solid var(--border)',
                transitionDelay: `${i * 0.1}s`,
              }}
            >
              <p className="font-fraunces text-3xl font-light text-charcoal">{s?.num}</p>
              <p className="text-xs font-semibold tracking-wide text-charcoal mt-1">{s?.label}</p>
              <p className="text-10 text-muted mt-0.5">{s?.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
